import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  type Identifier,
  useGetIdentity,
  useGetOne,
  useLocaleState,
  useNotify,
} from "ra-core";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Sale } from "../types";
import {
  ACCOUNT_CURRENCIES,
  type AccountCurrency,
  defaultCurrency,
  formatEstimate,
  territoryCountries,
} from "./accountCurrency";
import { useZones } from "./territories";

const useAccountSale = () => {
  const { identity } = useGetIdentity();
  return useGetOne<Sale>(
    "sales",
    { id: identity?.id as Identifier },
    { enabled: identity?.id != null },
  );
};

/** Cours de la BCE (unités pour un euro), remplacés chaque nuit par `radar rates`. */
export const useRates = () =>
  useQuery({
    queryKey: ["exchange_rates"],
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await getSupabaseClient()
        .from("exchange_rates")
        .select("currency, units_per_eur");
      if (error) throw error;
      return Object.fromEntries(
        (data ?? []).map((rate) => [rate.currency, Number(rate.units_per_eur)]),
      );
    },
    staleTime: 60 * 60 * 1000,
  });

/** Monnaie du compte (l'euro tant qu'elle n'est pas lue) et estimation « ≈ » d'un montant en euros. */
export const useAccountCurrency = () => {
  const { data: sale } = useAccountSale();
  const { data: rates = {} } = useRates();
  const [locale = "fr"] = useLocaleState();
  const currency: AccountCurrency = sale?.currency ?? "EUR";
  return {
    currency,
    estimate: (valueEur: number | null | undefined) =>
      formatEstimate(valueEur, currency, rates, locale),
  };
};

const useSaveCurrency = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  return async (value: AccountCurrency) => {
    const { error } = await getSupabaseClient().rpc("set_currency", { value });
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["sales"] });
  };
};

// Compte déjà traité dans ce chargement de page : la valeur par défaut n'est enregistrée qu'une fois.
let initialized: Identifier | null = null;

/** Fixe la monnaie d'un compte qui n'en a pas : territoires (ou zone d'intérêt d'un invité), sinon navigateur. */
export const AccountCurrencySync = () => {
  const { data: sale } = useAccountSale();
  const { data: zones } = useZones();
  const save = useSaveCurrency();
  const pending = !!sale && !sale.currency && initialized !== sale.id;
  const { data: territories } = useQuery({
    queryKey: ["user_territories", "own", sale?.id],
    enabled: pending && !!sale?.partner,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("user_territories")
        .select("country, zone")
        .eq("sales_id", sale!.id);
      if (error) throw error;
      return data ?? [];
    },
  });
  useEffect(() => {
    if (!pending || !zones || (sale.partner && !territories)) return;
    initialized = sale.id;
    const own = [
      ...(territories ?? []),
      { country: sale.interest_country, zone: sale.interest_zone },
    ];
    save(
      defaultCurrency({
        countries: territoryCountries(own, zones),
        world: !!sale.partner_world && sale.partner_offer === "subscriber",
        browser: navigator.languages ?? [navigator.language],
      }),
    );
  }, [pending, sale, zones, territories, save]);
  return null;
};

/** Sélecteur « € | $ » du menu du compte. */
export const CurrencyToggle = ({ className }: { className?: string }) => {
  const { currency } = useAccountCurrency();
  const save = useSaveCurrency();
  return (
    <ToggleGroup
      type="single"
      size="sm"
      variant="outline"
      value={currency}
      onValueChange={(value) => value && save(value as AccountCurrency)}
      className={className}
    >
      {ACCOUNT_CURRENCIES.map((code) => (
        <ToggleGroupItem
          key={code}
          value={code}
          aria-label={code === "EUR" ? "Euro" : "Dollar"}
          className="px-3 text-xs font-semibold"
        >
          {code === "EUR" ? "€" : "$"}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
};
